import { formatDistanceToNow } from 'date-fns';
import { MessageSquare } from 'lucide-react-native';
import { Pressable, StyleSheet } from 'react-native';
import { Box, HStack, Text, VStack } from '../../../components/primitives';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { WhatsAppConversation } from '../types';

interface ConversationListItemProps {
  conversation: WhatsAppConversation;
  onPress: (conversation: WhatsAppConversation) => void;
}

export function ConversationListItem({ conversation, onPress }: ConversationListItemProps) {
  const leadName = conversation.lead?.fullName || conversation.waId;
  const initials = (conversation.lead?.fullName ?? '?')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const timeAgo = conversation.lastMessageAt
    ? formatDistanceToNow(new Date(conversation.lastMessageAt), { addSuffix: true })
    : '';

  return (
    <Pressable onPress={() => onPress(conversation)} style={styles.container}>
      <HStack spacing="md" align="center" style={styles.inner}>
        <Box
          width={44}
          height={44}
          borderRadius="full"
          backgroundColor={colors.primary.main}
          alignItems="center"
          justifyContent="center"
        >
          <Text variant="caption" color={colors.text.inverse} style={styles.initials}>
            {initials}
          </Text>
        </Box>
        <VStack spacing="xxs" style={styles.content}>
          <HStack align="center" justify="between">
            <Text variant="body" color={colors.text.primary} style={styles.name} numberOfLines={1}>
              {leadName}
            </Text>
            {timeAgo ? (
              <Text variant="caption" color={colors.text.tertiary}>
                {timeAgo}
              </Text>
            ) : null}
          </HStack>
          <HStack align="center" justify="between">
            <HStack spacing="xs" align="center" style={styles.previewRow}>
              <MessageSquare size={12} color={colors.text.tertiary} />
              <Text
                variant="caption"
                color={colors.text.secondary}
                numberOfLines={1}
                style={styles.preview}
              >
                {conversation.lastMessagePreview || 'No messages yet'}
              </Text>
            </HStack>
            {conversation.unreadCount > 0 && (
              <Box
                backgroundColor={colors.primary.neon}
                borderRadius="full"
                minWidth={20}
                height={20}
                alignItems="center"
                justifyContent="center"
                style={styles.badge}
              >
                <Text variant="caption" color={colors.text.inverse} style={styles.badgeText}>
                  {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
                </Text>
              </Box>
            )}
          </HStack>
        </VStack>
      </HStack>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: tokens.radius.md,
    overflow: 'hidden',
  },
  inner: {
    padding: tokens.spacing.md,
    backgroundColor: colors.background.secondary,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: colors.border.muted,
  },
  initials: {
    fontSize: tokens.fontSize.sm,
    fontWeight: tokens.fontWeight.semibold,
  },
  content: {
    flex: 1,
  },
  name: {
    fontWeight: tokens.fontWeight.semibold,
    flex: 1,
  },
  previewRow: {
    flex: 1,
  },
  preview: {
    flex: 1,
  },
  badge: {
    paddingHorizontal: 6,
  },
  badgeText: {
    fontSize: 10,
    fontWeight: tokens.fontWeight.bold,
  },
});
